import { decodeUrlComponent } from "@/utils/url-utils";

/**
 * Synchronous fallback for the project/environment route params.
 *
 * Statically-rendered dashboard pages hydrate with an empty `router.query`
 * (Next fills it, and flips `router.isReady`, only after hydration), yet both
 * ids are already present in the URL path as `/projects/<id>` and
 * `/environments/<id>` segments. The connect transport has always read the
 * environment id straight from `window.location` for its header
 * (`parseEnvironmentIdFromUrl`); this parser applies the same idea to route
 * info so id-gated pages and queries do not have to wait for the router.
 *
 * Only these two params are parsed: they are the app-wide gating pair and the
 * only segment names that are unambiguous across the route tree (for example
 * `/deployments/<x>` maps to deploymentId on project pages but deploymentName
 * on Kubernetes pages, so generic segment parsing would misassign it).
 */
export interface PathnameRouteIds {
  environmentId?: string;
  projectId?: string;
}

const PROJECT_SEGMENT = /\/projects\/([^/?#]+)/;
const ENVIRONMENT_SEGMENT = /\/environments\/([^/?#]+)/;

const decodeSegment = (segment: string | undefined): string | undefined => {
  if (segment == null || segment.length === 0) {
    return undefined;
  }
  return decodeUrlComponent(segment);
};

export const parseRouteIdsFromPathname = (pathname: string): PathnameRouteIds => ({
  environmentId: decodeSegment(ENVIRONMENT_SEGMENT.exec(pathname)?.[1]),
  projectId: decodeSegment(PROJECT_SEGMENT.exec(pathname)?.[1]),
});
